import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../lib/api'
import DesignSupportLayout from './designSupport/DesignSupportLayout'

const CATALOG_SIZE = 20

const KCD_CHAPTER_CHIPS = [
  { label: 'A–B', q: 'A' },
  { label: 'C', q: 'C' },
  { label: 'D', q: 'D' },
  { label: 'E', q: 'E' },
  { label: 'I', q: 'I' },
  { label: 'J', q: 'J' },
  { label: 'K', q: 'K' },
  { label: 'M', q: 'M' },
  { label: 'N', q: 'N' },
]

const RIDER_QUICK_KEYS = ['E11', 'E10-E14', 'I10', 'I20-I25', 'C00-C97', 'J00-J99', 'K25-K27']

function suggestKcdCategoryKey(code) {
  const c = String(code || '').replace(/\./g, '').toUpperCase().trim()
  if (!c) return ''
  if (c.length >= 3) return c.slice(0, 3)
  return c
}

export default function DesignSupportDiseaseCodePage() {
  const [tab, setTab] = useState('code')
  const [q, setQ] = useState('')
  const [activeQuery, setActiveQuery] = useState('')
  const [catalogPage, setCatalogPage] = useState(1)
  const [catalogTotal, setCatalogTotal] = useState(0)
  const [suggestions, setSuggestions] = useState([])
  const [showSuggest, setShowSuggest] = useState(false)
  const [selected, setSelected] = useState(null)
  const [localErr, setLocalErr] = useState('')
  const [showCatalogHelp, setShowCatalogHelp] = useState(false)
  const [codeSearching, setCodeSearching] = useState(false)
  const searchRef = useRef(null)

  const [kcdCategory, setKcdCategory] = useState('')
  const [riderRows, setRiderRows] = useState([])
  const [riderLoading, setRiderLoading] = useState(false)
  const [riderErr, setRiderErr] = useState('')

  useEffect(() => {
    const onDown = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) setShowSuggest(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [])

  useEffect(() => {
    const v = String(activeQuery || '').trim()
    if (!v) {
      setSuggestions([])
      setCatalogTotal(0)
      setCodeSearching(false)
      return
    }
    setCodeSearching(true)
    setLocalErr('')
    api
      .get('/operator/design-support/kcd-catalog/search', {
        params: { q: v, page: catalogPage, size: CATALOG_SIZE },
      })
      .then((res) => {
        const data = res?.data?.data
        const items = Array.isArray(data?.items) ? data.items : []
        const total = Number(data?.total) || 0
        setSuggestions(items)
        setCatalogTotal(total)
        setShowSuggest(items.length > 0 && catalogPage === 1)
        if (items.length === 0) {
          setShowCatalogHelp(true)
          setLocalErr('카탈로그에 일치하는 상병이 없습니다.')
        } else {
          setShowCatalogHelp(false)
        }
      })
      .catch(() => {
        setSuggestions([])
        setCatalogTotal(0)
        setShowSuggest(false)
        setShowCatalogHelp(false)
        setLocalErr('검색에 실패했습니다.')
      })
      .finally(() => setCodeSearching(false))
  }, [activeQuery, catalogPage])

  const executeCodeSearch = useCallback(() => {
    const v = String(q).trim()
    if (!v) {
      setActiveQuery('')
      setCatalogPage(1)
      setSuggestions([])
      setShowSuggest(false)
      setShowCatalogHelp(false)
      setLocalErr('검색어를 입력한 뒤 검색을 눌러 주세요.')
      return
    }
    setCatalogPage(1)
    setActiveQuery(v)
  }, [q])

  const applyChapterChip = (prefix) => {
    setQ(prefix)
    setCatalogPage(1)
    setActiveQuery(prefix)
    setLocalErr('')
  }

  const pickSuggestion = (row) => {
    setSelected(row)
    setQ(`${row.code} ${row.nameKo || ''}`.trim())
    setShowSuggest(false)
    setKcdCategory(suggestKcdCategoryKey(row.code))
  }

  const loadRiders = useCallback(() => {
    const cat = String(kcdCategory || '').trim()
    if (!cat) {
      setRiderErr('KCD 구간·코드 접두(예: E11, C00-C97)를 입력하세요.')
      return
    }
    setRiderLoading(true)
    setRiderErr('')
    api
      .get('/operator/design-support/kcd-rider-mappings', { params: { kcdCategory: cat } })
      .then((res) => {
        const data = res?.data?.data
        setRiderRows(Array.isArray(data?.items) ? data.items : [])
      })
      .catch(() => {
        setRiderRows([])
        setRiderErr('매핑 조회에 실패했습니다.')
      })
      .finally(() => setRiderLoading(false))
  }, [kcdCategory])

  const applyRiderQuickKey = (key) => {
    setKcdCategory(key)
    setRiderErr('')
    setRiderLoading(true)
    api
      .get('/operator/design-support/kcd-rider-mappings', { params: { kcdCategory: key } })
      .then((res) => {
        const data = res?.data?.data
        setRiderRows(Array.isArray(data?.items) ? data.items : [])
      })
      .catch(() => {
        setRiderRows([])
        setRiderErr('매핑 조회에 실패했습니다.')
      })
      .finally(() => setRiderLoading(false))
  }

  const catalogPages = Math.max(1, Math.ceil(catalogTotal / CATALOG_SIZE))

  return (
    <DesignSupportLayout>
      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <h2 className="text-lg font-semibold text-slate-900">질병코드 · 특약 참고</h2>
        <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-relaxed text-amber-950 space-y-2">
          <p>
            본 화면은 <strong>보험사 약관을 대신하거나 가입 권유·보장을 확정하지 않습니다.</strong> 질병코드 검색은 운영 DB(
            <code className="text-xs">work_kcd_catalog</code>)이며, 특약 매핑은 <code className="text-xs">work_kcd_rider_mapping</code>에 정리된 자료만
            표시합니다. <strong>실제 보장은 해당 계약 약관·보험사 안내가 우선</strong>합니다.
          </p>
        </div>

        <div className="mt-6 flex flex-wrap gap-2 border-b border-slate-200 pb-2 text-sm">
          <button
            type="button"
            className={tab === 'code' ? 'font-semibold text-brand-navy border-b-2 border-brand-sky -mb-2.5 px-2' : 'text-slate-600 px-2 hover:text-slate-900'}
            onClick={() => setTab('code')}
          >
            질병코드 검색
          </button>
          <button
            type="button"
            className={tab === 'rider' ? 'font-semibold text-brand-navy border-b-2 border-brand-sky -mb-2.5 px-2' : 'text-slate-600 px-2 hover:text-slate-900'}
            onClick={() => setTab('rider')}
          >
            특약 매핑 참고
          </button>
          <Link to="/design/surgery-search" className="ml-auto self-center text-xs font-medium text-brand-sky hover:underline">
            수술명 · 종 참고 →
          </Link>
        </div>

        {tab === 'code' && (
          <div className="mt-6 space-y-4">
            <div>
              <p className="text-xs font-medium text-slate-600">코드 접두 빠른 필터</p>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {KCD_CHAPTER_CHIPS.map((ch) => (
                  <button
                    key={ch.q}
                    type="button"
                    className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-700 hover:border-brand-sky hover:bg-white"
                    onClick={() => applyChapterChip(ch.q)}
                  >
                    {ch.label}
                  </button>
                ))}
              </div>
            </div>

            <div ref={searchRef} className="relative">
              <label className="block text-sm font-medium text-slate-700">질병명 또는 코드</label>
              <div className="mt-1 flex flex-wrap gap-2">
                <input
                  className="min-w-[12rem] flex-1 rounded border border-slate-300 px-3 py-2 text-sm focus:border-brand-sky focus:outline-none focus:ring-1 focus:ring-brand-sky"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      executeCodeSearch()
                    }
                  }}
                  placeholder="예: 당뇨, I10, 백내장"
                  autoComplete="off"
                />
                <button
                  type="button"
                  className="rounded bg-brand-navy px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
                  onClick={executeCodeSearch}
                  disabled={codeSearching}
                >
                  {codeSearching ? '검색 중…' : '검색'}
                </button>
              </div>
              {localErr && <p className="mt-1 text-xs text-red-600">{localErr}</p>}
              {showCatalogHelp && (
                <p className="mt-1 text-xs text-slate-600">
                  <Link to="/data/design-support-reference" className="text-blue-700 underline">
                    데이터 적재
                  </Link>
                  에서 KCD CSV를 올리면 결과가 채워집니다.
                </p>
              )}
              {showSuggest && suggestions.length > 0 && catalogPage === 1 && (
                <ul className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded border border-slate-200 bg-white shadow-md">
                  {suggestions.map((row) => (
                    <li key={row.id}>
                      <button
                        type="button"
                        className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50"
                        onClick={() => pickSuggestion(row)}
                      >
                        <span className="font-mono text-xs text-slate-500">{row.code}</span>
                        <span className="ml-2 text-slate-900">{row.nameKo}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {activeQuery && suggestions.length > 0 && (
              <div>
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-600">
                  <span>
                    검색 결과 <strong>{catalogTotal.toLocaleString()}</strong>건
                  </span>
                  {catalogPages > 1 && (
                    <span>
                      {catalogPage} / {catalogPages} 페이지
                    </span>
                  )}
                </div>
                <div className="overflow-x-auto rounded border border-slate-200">
                  <table className="min-w-full text-sm">
                    <thead className="bg-slate-50 text-left text-xs font-semibold text-slate-600">
                      <tr>
                        <th className="px-3 py-2">코드</th>
                        <th className="px-3 py-2">한글명</th>
                        <th className="px-3 py-2">영문</th>
                      </tr>
                    </thead>
                    <tbody>
                      {suggestions.map((row) => (
                        <tr key={row.id} className="border-t border-slate-100">
                          <td className="px-3 py-2 font-mono text-xs">
                            <button type="button" className="text-brand-sky hover:underline" onClick={() => pickSuggestion(row)}>
                              {row.code}
                            </button>
                          </td>
                          <td className="px-3 py-2 text-slate-900">{row.nameKo}</td>
                          <td className="px-3 py-2 text-xs text-slate-500">{row.nameEn || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {catalogPages > 1 && (
                  <div className="mt-3 flex items-center gap-2">
                    <button
                      type="button"
                      className="rounded border border-slate-300 px-3 py-1.5 text-xs hover:bg-slate-50 disabled:opacity-40"
                      disabled={catalogPage <= 1 || codeSearching}
                      onClick={() => setCatalogPage((p) => Math.max(1, p - 1))}
                    >
                      이전
                    </button>
                    <button
                      type="button"
                      className="rounded border border-slate-300 px-3 py-1.5 text-xs hover:bg-slate-50 disabled:opacity-40"
                      disabled={catalogPage >= catalogPages || codeSearching}
                      onClick={() => setCatalogPage((p) => Math.min(catalogPages, p + 1))}
                    >
                      다음
                    </button>
                  </div>
                )}
              </div>
            )}

            {selected && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm">
                <div className="font-mono text-lg font-semibold text-brand-navy">{selected.code}</div>
                <div className="mt-1 text-slate-800">{selected.nameKo}</div>
                {selected.nameEn && <div className="mt-1 text-xs text-slate-500">{selected.nameEn}</div>}
                <p className="mt-3 text-xs text-slate-600">
                  특약 탭에서 사용할 KCD 키 제안: <span className="font-mono font-semibold">{suggestKcdCategoryKey(selected.code)}</span>
                </p>
              </div>
            )}
          </div>
        )}

        {tab === 'rider' && (
          <div className="mt-6 space-y-4">
            <div>
              <p className="text-xs font-medium text-slate-600">자주 쓰는 매핑 키</p>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {RIDER_QUICK_KEYS.map((key) => (
                  <button
                    key={key}
                    type="button"
                    className={`rounded-full border px-2.5 py-1 text-xs font-medium transition ${
                      kcdCategory === key ? 'border-brand-navy bg-sky-50 text-brand-navy ring-1 ring-brand-navy/10' : 'border-slate-200 bg-slate-50 text-slate-700 hover:border-brand-sky'
                    }`}
                    onClick={() => applyRiderQuickKey(key)}
                  >
                    {key}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">KCD 매핑 키</label>
              <div className="mt-1 flex flex-wrap gap-2">
                <input
                  className="min-w-[12rem] flex-1 rounded border border-slate-300 px-3 py-2 text-sm focus:border-brand-sky focus:outline-none focus:ring-1 focus:ring-brand-sky"
                  value={kcdCategory}
                  onChange={(e) => setKcdCategory(e.target.value)}
                  placeholder="예: E11, C00-C97"
                />
                <button
                  type="button"
                  className="rounded bg-brand-navy px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
                  onClick={loadRiders}
                  disabled={riderLoading}
                >
                  {riderLoading ? '조회 중…' : '조회'}
                </button>
              </div>
              {riderErr && <p className="mt-1 text-xs text-red-600">{riderErr}</p>}
              <p className="mt-2 text-xs text-slate-500">
                DB에 동일한 <code>kcd_category</code>가 있을 때만 행이 반환됩니다.
              </p>
            </div>

            <div className="overflow-x-auto rounded border border-slate-200">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs font-semibold text-slate-600">
                  <tr>
                    <th className="px-3 py-2">KCD</th>
                    <th className="px-3 py-2">특약</th>
                    <th className="px-3 py-2">보장 요지</th>
                    <th className="px-3 py-2">매칭</th>
                    <th className="px-3 py-2">비고</th>
                  </tr>
                </thead>
                <tbody>
                  {riderLoading ? (
                    <tr>
                      <td colSpan={5} className="px-3 py-8 text-center text-slate-500">
                        불러오는 중…
                      </td>
                    </tr>
                  ) : riderRows.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-3 py-8 text-center text-slate-500">
                        조회 결과가 없습니다. 키를 바꾸거나 데이터 적재에서 매핑 CSV를 확인하세요.
                      </td>
                    </tr>
                  ) : (
                    riderRows.map((r) => (
                      <tr key={r.id} className="border-t border-slate-100">
                        <td className="px-3 py-2 font-mono text-xs">{r.kcdCategory}</td>
                        <td className="px-3 py-2">
                          <div className="font-medium text-slate-900">{r.riderTypeName}</div>
                          <div className="text-xs text-slate-500">{r.riderTypeCode}</div>
                        </td>
                        <td className="max-w-xs px-3 py-2 text-xs text-slate-600">{r.riderCoverageSummary || '—'}</td>
                        <td className="px-3 py-2 text-xs">{r.matchType || '—'}</td>
                        <td className="px-3 py-2 text-xs text-slate-600">{r.notes || '—'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </DesignSupportLayout>
  )
}
